import { z } from 'zod';

export const cellRowTypeSchema = z.enum(['activation', 'dimension']);
export type CellRowType = z.infer<typeof cellRowTypeSchema>;

/**
 * Where a version's content came from — replaces the old per-cell
 * `isManuallyEdited` boolean (2026-08-06 design doc, M10). Rendered as a
 * label in the drawer's Historique tab ("modifiée manuellement" vs.
 * generated). A restore (M9) copies the restored version's own origin, so
 * there is deliberately no fourth `restored` value.
 */
export const cellVersionOriginSchema = z.enum(['seed', 'manual', 'regenerated']);
export type CellVersionOrigin = z.infer<typeof cellVersionOriginSchema>;

export const cellVersionSchema = z.object({
  id: z.string().uuid(),
  cellId: z.string().uuid(),
  content: z.string(),
  origin: cellVersionOriginSchema,
  createdAt: z.string().datetime(),
  createdBy: z.string().uuid().nullable(),
  /**
   * The regeneration batch that produced this version (2026-08-14 design
   * doc) — only ever set for `origin: 'regenerated'` versions. Lets the
   * drawer offer "restore this whole regeneration" next to the relevant
   * version instead of only a per-cell restore.
   */
  batchId: z.string().uuid().nullable(),
});
export type CellVersion = z.infer<typeof cellVersionSchema>;

/**
 * One cell of the generated grid, with its *full* version history resolved
 * inline (`versions`), not just the current pointer — the drawer's
 * versioning tab (2026-08-06 drawer design, decision D1) must show every
 * version of a selected cell, and a manual edit (D10) grows that list to 2+
 * entries. `versions` is ordered newest-first. `currentVersionId` is `null`
 * for a cell that has never been generated/seeded at all (this pass always
 * seeds/fabricates content, so in practice every cell has at least one
 * version, but the shape stays honest about the possibility). The grid
 * renders whichever entry in `versions` has `id === currentVersionId`.
 */
export const cellSchema = z.object({
  id: z.string().uuid(),
  matrixId: z.string().uuid(),
  rowType: cellRowTypeSchema,
  /** Levier `order` (1-6, as a string) for `activation`; `dimensions.id` (uuid) for `dimension`. */
  rowRef: z.string(),
  columnNumber: z.number().int(),
  currentVersionId: z.string().uuid().nullable(),
  /** Newest-first. Empty array for a cell that has never been generated/seeded. */
  versions: z.array(cellVersionSchema),
});
export type Cell = z.infer<typeof cellSchema>;

/** `GET /matrices/:id/cells` response — the full resolved grid, unordered (client groups by row/column). */
export const matrixCellsSchema = z.array(cellSchema);
export type MatrixCells = z.infer<typeof matrixCellsSchema>;

/**
 * `POST /cells/:id/versions` body — a manual edit. Always creates a new
 * `cell_versions` row and repoints `cells.currentVersionId`; never mutates
 * an existing version in place (append-only per the data model).
 */
export const createCellVersionInputSchema = z.object({
  content: z.string().min(1).max(20_000),
});
export type CreateCellVersionInput = z.infer<typeof createCellVersionInputSchema>;

/**
 * `POST /cells/:id/versions/:versionId/restore` — non-destructive restore
 * (2026-08-06 design doc, M9, following `plan-mss.md` §8): inserts a *new*
 * `cell_versions` row copying the target version's content and origin, then
 * repoints `currentVersionId`. Nothing is ever deleted, so the user can
 * always move forward again. No body — the target is fully identified by
 * the URL.
 */
export const restoredCellSchema = cellSchema;
export type RestoredCell = z.infer<typeof restoredCellSchema>;
